import React, { useEffect, useState, useRef } from "react";
import styled from "styled-components";
import {
  FileText,
  Upload,
  Trash2,
  Loader2,
  AlertCircle,
  CheckCircle2,
  Clock,
  ArrowDownToLine,
} from "lucide-react";
import DashboardLayout from "layouts/DashboardLayout";
import { documentsService } from "services/documents.service";
import type { Document, DocumentType } from "types/document.types";
import { EmptyState, LoadingSpinner } from "components/dashboard";

const C_PRIMARY = "#ff6c00";

// ─── Tabs ─────────────────────────────────────────────────────────────────────

const TabBar = styled.div`
  display: flex;
  border-bottom: 2px solid #e5e7eb;
  margin-bottom: 1.5rem;
`;
const Tab = styled.button<{ $active: boolean }>`
  padding: 0.65rem 1.25rem;
  font-size: 0.8rem;
  font-weight: 700;
  border: none;
  background: none;
  cursor: pointer;
  color: ${(p) => (p.$active ? C_PRIMARY : "#6b7280")};
  border-bottom: 2px solid ${(p) => (p.$active ? C_PRIMARY : "transparent")};
  margin-bottom: -2px;
  transition:
    color 0.15s,
    border-color 0.15s;
  display: flex;
  align-items: center;
  gap: 0.5rem;
`;
const TabCount = styled.span<{ $active: boolean }>`
  font-size: 0.6rem;
  font-weight: 800;
  padding: 1px 6px;
  background: ${(p) => (p.$active ? "#fff3e8" : "#f3f4f6")};
  color: ${(p) => (p.$active ? C_PRIMARY : "#9ca3af")};
`;

const Grid = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(280px, 1fr));
  gap: 1px;
  background: #e5e7eb;
  border: 1px solid #e5e7eb;
`;
const Card = styled.div`
  background: white;
  padding: 1.25rem;
  display: flex;
  flex-direction: column;
  gap: 0.75rem;
`;
const DocName = styled.p`
  font-size: 0.9rem;
  font-weight: 700;
  color: #111;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
`;
const DocType = styled.p`
  font-size: 0.7rem;
  text-transform: uppercase;
  letter-spacing: 0.08em;
  color: #9ca3af;
  font-weight: 600;
`;
const StatusChip = styled.span<{ $status: string }>`
  font-size: 0.65rem;
  font-weight: 700;
  text-transform: uppercase;
  letter-spacing: 0.08em;
  padding: 2px 8px;
  background: ${(p) =>
    p.$status === "valide"
      ? "#dcfce7"
      : p.$status === "rejete"
        ? "#fee2e2"
        : "#f3f4f6"};
  color: ${(p) =>
    p.$status === "valide"
      ? "#16a34a"
      : p.$status === "rejete"
        ? "#dc2626"
        : "#6b7280"};
`;

const UploadBox = styled.div`
  border: 2px dashed #e5e7eb;
  padding: 2rem;
  text-align: center;
  cursor: pointer;
  transition: all 0.2s;
  background: white;
  &:hover {
    border-color: ${C_PRIMARY};
    background: #fff8f3;
  }
`;

const UploadForm = styled.form`
  background: white;
  border: 1px solid #e5e7eb;
  padding: 1.5rem;
  margin-bottom: 2rem;
`;
const FormRow = styled.div`
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 1rem;
  @media (max-width: 640px) {
    grid-template-columns: 1fr;
  }
`;
const Label = styled.label`
  font-size: 0.7rem;
  font-weight: 700;
  text-transform: uppercase;
  letter-spacing: 0.08em;
  color: #374151;
  display: block;
  margin-bottom: 0.4rem;
`;
const Select = styled.select`
  width: 100%;
  padding: 0.75rem;
  border: 1.5px solid #e5e7eb;
  font-size: 0.875rem;
  color: #111;
  &:focus {
    outline: none;
    border-color: ${C_PRIMARY};
  }
`;
const SubmitBtn = styled.button`
  display: flex;
  align-items: center;
  gap: 0.5rem;
  background: ${C_PRIMARY};
  color: white;
  border: none;
  padding: 0.75rem 1.5rem;
  font-size: 0.875rem;
  font-weight: 700;
  cursor: pointer;
  margin-top: 1rem;
  &:disabled {
    opacity: 0.5;
    cursor: not-allowed;
  }
`;

const DOC_TYPE_LABELS: Record<DocumentType, string> = {
  passeport: "Passeport",
  releve_notes: "Relevé de notes",
  diplome: "Diplôme",
  lettre_motivation: "Lettre de motivation",
  photo_identite: "Photo d'identité",
  justificatif_domicile: "Justificatif de domicile",
  extrait_naissance: "Extrait de naissance",
  certificat_scolarite: "Certificat de scolarité",
  autre: "Autre",
};

const DocumentsPage: React.FC = () => {
  const [docs, setDocs] = useState<Document[]>([]);
  const [loading, setLoading] = useState(true);
  const [uploading, setUploading] = useState(false);
  const [selectedType, setSelectedType] = useState<DocumentType>("passeport");
  const [selectedFile, setSelectedFile] = useState<File | null>(null);
  const [error, setError] = useState("");
  const [tab, setTab] = useState<"envoyes" | "recus">("envoyes");
  const fileRef = useRef<HTMLInputElement>(null);

  const load = () => {
    documentsService.getMyDocuments().then((d) => {
      setDocs(d);
      setLoading(false);
    });
  };

  useEffect(() => {
    load();
  }, []);

  const handleUpload = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!selectedFile) {
      setError("Veuillez sélectionner un fichier.");
      return;
    }
    setUploading(true);
    setError("");
    try {
      await documentsService.uploadDocument({
        type: selectedType,
        file: selectedFile,
      });
      setSelectedFile(null);
      if (fileRef.current) fileRef.current.value = "";
      load();
    } catch {
      setError("Erreur lors de l'envoi. Réessayez.");
    } finally {
      setUploading(false);
    }
  };

  const handleDelete = async (id: number) => {
    if (!window.confirm("Supprimer ce document ?")) return;
    await documentsService.deleteDocument(id);
    setDocs((prev) => prev.filter((d) => d.id !== id));
  };

  // Séparation par source
  const envoyesDocs = docs.filter((d) => !d.source || d.source === "student");
  const recusDocs = docs.filter((d) => d.source === "momseduc");
  const visibleDocs = tab === "envoyes" ? envoyesDocs : recusDocs;

  return (
    <DashboardLayout title="Documents">
      <UploadForm onSubmit={handleUpload}>
        <p style={{ fontWeight: 800, color: "#111", marginBottom: "1rem" }}>
          <Upload size={16} style={{ display: "inline", marginRight: 6 }} />
          Envoyer un document
        </p>
        <FormRow>
          <div>
            <Label htmlFor="docType">Type de document</Label>
            <Select
              id="docType"
              value={selectedType}
              onChange={(e) => setSelectedType(e.target.value as DocumentType)}
            >
              {Object.entries(DOC_TYPE_LABELS).map(([v, l]) => (
                <option key={v} value={v}>
                  {l}
                </option>
              ))}
            </Select>
          </div>
          <div>
            <Label>Fichier (PDF, JPG, PNG)</Label>
            <UploadBox onClick={() => fileRef.current?.click()}>
              {selectedFile ? (
                <span
                  style={{
                    fontSize: "0.85rem",
                    color: C_PRIMARY,
                    fontWeight: 600,
                  }}
                >
                  {selectedFile.name}
                </span>
              ) : (
                <span style={{ fontSize: "0.8rem", color: "#9ca3af" }}>
                  Cliquez pour choisir un fichier
                </span>
              )}
              <input
                ref={fileRef}
                type="file"
                accept=".pdf,.jpg,.jpeg,.png"
                style={{ display: "none" }}
                onChange={(e) => setSelectedFile(e.target.files?.[0] ?? null)}
              />
            </UploadBox>
          </div>
        </FormRow>
        {error && (
          <p
            style={{
              color: "#dc2626",
              fontSize: "0.8rem",
              marginTop: "0.5rem",
            }}
          >
            <AlertCircle
              size={13}
              style={{ display: "inline", marginRight: 4 }}
            />
            {error}
          </p>
        )}
        <SubmitBtn type="submit" disabled={uploading}>
          {uploading ? <Loader2 size={15} /> : <Upload size={15} />}
          {uploading ? "Envoi…" : "Envoyer"}
        </SubmitBtn>
      </UploadForm>

      {/* Tab bar */}
      <TabBar>
        <Tab $active={tab === "envoyes"} onClick={() => setTab("envoyes")}>
          <Upload size={13} />
          Documents envoyés
          <TabCount $active={tab === "envoyes"}>{envoyesDocs.length}</TabCount>
        </Tab>
        <Tab $active={tab === "recus"} onClick={() => setTab("recus")}>
          <ArrowDownToLine size={13} />
          Documents reçus
          <TabCount $active={tab === "recus"}>{recusDocs.length}</TabCount>
        </Tab>
      </TabBar>

      {/* Contenu du tab */}
      {loading ? (
        <LoadingSpinner />
      ) : visibleDocs.length === 0 ? (
        <EmptyState
          icon={<FileText size={40} />}
          title={
            tab === "envoyes" ? "Aucun document envoyé" : "Aucun document reçu"
          }
          description={
            tab === "envoyes"
              ? "Utilisez le formulaire ci-dessus pour envoyer vos documents."
              : "MomsEduc n'a pas encore partagé de document avec vous."
          }
        />
      ) : (
        <Grid>
          {visibleDocs.map((doc) => (
            <Card key={doc.id}>
              <div
                style={{ display: "flex", alignItems: "center", gap: "0.5rem" }}
              >
                <FileText size={18} color={C_PRIMARY} />
                <div style={{ flex: 1, minWidth: 0 }}>
                  <DocName>{doc.originalName}</DocName>
                  <DocType>{DOC_TYPE_LABELS[doc.type] ?? doc.type}</DocType>
                </div>
              </div>
              <div
                style={{
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "space-between",
                }}
              >
                <StatusChip $status={doc.status}>
                  {doc.status === "valide" ? (
                    <>
                      <CheckCircle2 size={10} style={{ display: "inline" }} />{" "}
                      Validé
                    </>
                  ) : doc.status === "rejete" ? (
                    <>
                      <AlertCircle size={10} style={{ display: "inline" }} />{" "}
                      Rejeté
                    </>
                  ) : (
                    <>
                      <Clock size={10} style={{ display: "inline" }} /> En
                      attente
                    </>
                  )}
                </StatusChip>
                {tab === "envoyes" && (
                  <button
                    onClick={() => handleDelete(doc.id)}
                    style={{
                      background: "none",
                      border: "none",
                      cursor: "pointer",
                      color: "#9ca3af",
                      display: "flex",
                    }}
                    title="Supprimer"
                  >
                    <Trash2 size={15} />
                  </button>
                )}
              </div>
              {doc.rejectionReason && (
                <p
                  style={{
                    fontSize: "0.75rem",
                    color: "#dc2626",
                    background: "#fee2e2",
                    padding: "4px 8px",
                  }}
                >
                  {doc.rejectionReason}
                </p>
              )}
              <p style={{ fontSize: "0.7rem", color: "#9ca3af" }}>
                {tab === "envoyes" ? "Envoyé" : "Reçu"} le{" "}
                {new Date(doc.uploadedAt).toLocaleDateString("fr-FR")}
              </p>
            </Card>
          ))}
        </Grid>
      )}
    </DashboardLayout>
  );
};

export default DocumentsPage;
